import { f as formatMoney } from './currency-BS0riN3f.js';

/**
 * @class rebuyGiftWithPurchase
 * @description Syncs the gift-with-purchase (GWP) Cross-Sell widget, configured
 * in Rebuy's dashboard, with the live Smart Cart subtotal. Reference:
 * https://developers.rebuyengine.com/reference/smart-cart-event-listeners/
 */
class rebuyGiftWithPurchase {
  // Smart Cart events that should trigger a full re-evaluation of GWP eligibility
  // (button disabled state + "spend more" message) for every item in the widget.
  // Only 'rebuy:smartcart.show' is active for now — the line-item events below
  // are commented out because the cart already re-fires 'show' often enough in
  // practice; enable them individually if a specific interaction stops updating.
  // 'rebuy:smartcart.line-item-increase',
  // 'rebuy:smartcart.line-item-decrease',
  // 'rebuy:smartcart.line-item-removed',
  // 'rebuy:smartcart.item-switch-to-subscription',
  // 'rebuy:smartcart.item-switch-to-one-time',
  static RECOMPUTE_EVENTS = ['rebuy:smartcart.show'];

  /**
   * @method init
   * @description Entry point. Waits for Rebuy's Smart Cart instance to be ready
   * before doing anything, since `smartcart` (and its API methods used below)
   * don't exist yet on the initial page load.
   * @returns {void}
   */
  init() {
    document.addEventListener('rebuy:smartcart.ready', (event) => this.onSmartCartReady(event));
    document.addEventListener('click', (event) => this.onAddButtonClick(event), true);
  }

  /**
   * @method onSmartCartReady
   * @description Fired once by Rebuy when the Smart Cart instance is ready.
   * Subscribes to the recompute events (so GWP state stays in sync as the cart
   * changes) and runs an initial evaluation immediately, since the cart may
   * already contain items on page load (e.g. after a refresh).
   * @param {CustomEvent} event - Rebuy's `rebuy:smartcart.ready` event.
   * @returns {void}
   */
  onSmartCartReady(event) {
    const { smartcart } = event.detail;

    rebuyGiftWithPurchase.RECOMPUTE_EVENTS.forEach((eventName) => {
      document.addEventListener(eventName, (recomputeEvent) => {
        this.evaluateGiftWithProductState(recomputeEvent.detail.smartcart);
      });
    });

    this.evaluateGiftWithProductState(smartcart);
  }

  /**
   * @method onAddButtonClick
   * @description Closes the race condition where a customer could click two
   * different gifts' Add buttons before Rebuy's add-to-cart request round
   * trips and `evaluateGiftWithProductState` gets a chance to re-disable everything
   * (`rebuy:smartcart.show` only fires after that request settles). Registered
   * on `document` with capture, so it runs before Rebuy's own click handler on
   * the button and locks every other gift's Add button immediately, before the
   * clicked one's request even starts.
   * @param {MouseEvent} event - The captured click event.
   * @returns {void}
   */
  onAddButtonClick(event) {
    const clickedButton = event.target.closest('.rebuy-button');
    if (!clickedButton) return;

    const root = this.findWidgetRoot();
    if (!root || !root.contains(clickedButton)) return;

    const clickedItemEl = clickedButton.closest('.rebuy-product-block');
    const clickedProductId = clickedItemEl ? this.getProductIdFromItemEl(clickedItemEl) : null;
    if (clickedProductId === null || this.getTierPriceCents(clickedProductId) === null) return;

    this.getWidgetProductItems(root).forEach((itemEl) => {
      const addButton = itemEl.querySelector('.rebuy-button');
      if (!addButton || addButton === clickedButton) return;

      const productId = this.getProductIdFromItemEl(itemEl);
      if (productId === null || this.getTierPriceCents(productId) === null) return;

      addButton.disabled = true;
      addButton.classList.add('is-disabled');
    });
  }

  /**
   * @method evaluateGiftWithProductState
   * @description Recomputes the entire GWP state for the current cart: removes
   * gift line items that have fallen below their tier threshold, enforces only
   * a single free gift in cart at a time, shows/hides the whole widget, and
   * updates each gift item's Add button (enabled/disabled) and "spend X more"
   * message based on whether the current subtotal meets that item's own tier
   * price.
   * @param {object} smartcart - Rebuy's Smart Cart API instance.
   * @returns {void}
   */
  evaluateGiftWithProductState(smartcart) {
    if (!smartcart) return;

    try {
      const subtotalCents = this.getSubtotalCents(smartcart);
      const cartItems = smartcart.items?.() || [];

      // Drop any gift line item that no longer qualifies (subtotal dropped below
      // its tier), then make sure at most one free gift remains in the cart.
      const eligibleCartItems = this.enforceSingleFreeGift(
        smartcart,
        this.removeIneligibleGifts(smartcart, cartItems)
      );

      const isGiftInCart = eligibleCartItems.some((item) => this.isFreeGiftItem(item));

      // Once a gift has been added, hide the whole cross-sell widget rather than
      // showing every other gift as "already added"/disabled.
      this.toggleWidgetVisibility(isGiftInCart);

      const root = this.findWidgetRoot();
      // Widget hasn't rendered into the DOM yet (e.g. this fired before Rebuy's
      // own async widget render completed) — nothing to update this pass.
      if (!root) return;

      this.getWidgetProductItems(root).forEach((itemEl) => {
        const productId = this.getProductIdFromItemEl(itemEl);
        if (!productId) return;

        const tierPriceCents = this.getTierPriceCents(productId);

        // Not one of our configured GWP products (e.g. a regular cross-sell item in the same widget) — leave it alone.
        if (tierPriceCents === null) return;

        // console.log(
        //   `> GWP item ${productId}: tierPriceCents=${tierPriceCents}, subtotalCents=${subtotalCents}`
        // );
        this.applyItemState(itemEl, tierPriceCents, subtotalCents, isGiftInCart);
      });
    } catch (error) {
      console.error('rebuy-gift-with-purchase: failed to sync GWP widget state', error);
    }
  }

  /**
   * @method getSubtotalCents
   * @description Reads the cart subtotal from Rebuy's Smart Cart API.
   * `subtotalNumber()` returns an integer in cents (e.g. 4999 for €49.99),
   * which is the same unit as `tierPriceCents` from `window.rebuyOverwrites`,
   * so the two can be compared directly without conversion.
   * @param {object} smartcart - Rebuy's Smart Cart API instance.
   * @returns {number} Cart subtotal in cents.
   */
  getSubtotalCents(smartcart) {
    const subtotal = smartcart.subtotalNumber();
    return typeof subtotal === 'number' ? subtotal : 0;
  }

  /**
   * @method removeIneligibleGifts
   * @description Filters cart line items down to those that should stay,
   * removing any gift line item (identified via its tier price config) whose
   * product tier price is no longer covered by the current subtotal — e.g. the
   * customer removed enough from the cart after the gift was added. Non-GWP
   * items are always kept untouched.
   * @param {object} smartcart - Rebuy's Smart Cart API instance.
   * @param {Array<object>} cartItems - Current cart line items.
   * @returns {Array<object>} Cart items that remain eligible/untouched.
   */
  removeIneligibleGifts(smartcart, cartItems) {
    return cartItems.filter((item) => {
      const tierPriceCents = this.getTierPriceCents(String(item.product_id));
      // Not one of our configured GWP products — leave it alone.
      if (tierPriceCents === null) return true;
      if (this.getSubtotalCents(smartcart) >= tierPriceCents) return true;

      smartcart.removeItem(item, () => undefined);
      return false;
    });
  }

  /**
   * @method isFreeGiftItem
   * @description Checks whether a cart line item is a free gift added through
   * this specific GWP widget: it carries the widget's component id in its line
   * item properties, and its price is fully discounted (price === total_discount).
   * @param {object} item - A Smart Cart line item.
   * @returns {boolean} True if this line item is a free gift from this widget.
   */
  isFreeGiftItem(item) {
    return (
      String(item.properties?._widget_id) === String(window.rebuyOverwrites.widgetComponentId) &&
      item.price === item.total_discount
    );
  }

  /**
   * @method enforceSingleFreeGift
   * @description Only one free gift is allowed in the cart at a time. If more
   * than one somehow ended up in the cart, keeps the most valuable one and
   * removes the rest.
   * @param {object} smartcart - Rebuy's Smart Cart API instance.
   * @param {Array<object>} cartItems - Current cart line items.
   * @returns {Array<object>} Cart items with any extra free gifts removed.
   */
  enforceSingleFreeGift(smartcart, cartItems) {
    const freeGiftItems = cartItems.filter((item) => this.isFreeGiftItem(item));
    if (freeGiftItems.length <= 1) return cartItems;

    const [keptGift, ...cheaperGifts] = [...freeGiftItems].sort((a, b) => b.price - a.price);
    cheaperGifts.forEach((item) => smartcart.removeItem(item, () => undefined));

    return cartItems.filter((item) => item === keptGift || !cheaperGifts.includes(item));
  }

  /**
   * @method toggleWidgetVisibility
   * @description Shows or hides the entire GWP cross-sell widget element.
   * @param {boolean} hide - Whether the widget should be hidden (true) or shown (false).
   * @returns {void}
   */
  toggleWidgetVisibility(hide) {
    const widgetEl = document.getElementById(
      `rebuy-widget-${window.rebuyOverwrites.widgetComponentId}`
    );
    if (!widgetEl) return;

    widgetEl.style.display = hide ? 'none' : 'block';
  }

  /**
   * @method findWidgetRoot
   * @description Resolves the GWP widget's root DOM element. Rebuy renders the
   * widget container with an id of the form `rebuy-widget-<componentId>`, so
   * this is a plain lookup rather than anything involving the Smart Cart API.
   * @returns {HTMLElement|null} The widget root element, or null if not yet rendered.
   */
  findWidgetRoot() {
    return document.getElementById(`rebuy-widget-${window.rebuyOverwrites.widgetComponentId}`);
  }

  /**
   * @method getWidgetProductItems
   * @description Collects every gift product block rendered inside the widget.
   * Rebuy renders each gift as a `.rebuy-product-block` element — there is no
   * `data-product-id` attribute on it (that would be too easy); the product id
   * is embedded in one of its classes instead (see `getProductIdFromItemEl`).
   * @param {HTMLElement} root - The widget root element from `findWidgetRoot`.
   * @returns {HTMLElement[]} All gift item elements currently rendered.
   */
  getWidgetProductItems(root) {
    return Array.from(root.querySelectorAll('.rebuy-product-block'));
  }

  /**
   * @method getProductIdFromItemEl
   * @description Extracts the Shopify product id from a rendered gift item
   * element. Rebuy encodes the product id as a `product-id-<id>` class on the
   * `.rebuy-product-block` element (e.g. `product-id-7794327748780`) rather
   * than as a data attribute, so this pulls it out of the class list.
   * @param {HTMLElement} itemEl - A gift item element from `getWidgetProductItems`.
   * @returns {string|null} The product id as a string, or null if not found.
   */
  getProductIdFromItemEl(itemEl) {
    const productIdClass = Array.from(itemEl.classList).find((cls) => /^product-id-\d+$/.test(cls));
    return productIdClass ? productIdClass.replace('product-id-', '') : null;
  }

  /**
   * @method getTierPriceCents
   * @description Looks up the configured tier price (in cents) for a given
   * product id from `window.rebuyOverwrites.products` (populated in
   * `rebuy-gwp-config.liquid` from each product's `gift.cents` metafield).
   * Returns null for products that aren't part of the configured GWP
   * collection at all, which is used elsewhere to distinguish "not eligible
   * yet" from "not one of ours".
   * @param {string} productId - Shopify product id as a string.
   * @returns {number|null} Tier price in cents, or null if not a configured GWP product.
   */
  getTierPriceCents(productId) {
    const product = window.rebuyOverwrites.products.find((entry) => String(entry.id) === productId);
    return product ? product.tierPriceCents : null;
  }

  /**
   * @method applyItemState
   * @description Applies the computed eligibility state to a single gift
   * item's DOM: toggles its Add button's disabled state, and sets the
   * accompanying message to either the "already added" notice, an empty string
   * (fully eligible), or the "spend X more" copy with the remaining amount
   * filled in.
   * @param {HTMLElement} itemEl - The gift item element to update.
   * @param {number} tierPriceCents - This item's own required subtotal, in cents.
   * @param {number} subtotalCents - Current cart subtotal, in cents.
   * @param {boolean} anyGiftInCart - Whether a free gift is already in the cart.
   * @returns {void}
   */
  applyItemState(itemEl, tierPriceCents, subtotalCents, anyGiftInCart) {
    const addButton = itemEl.querySelector('.rebuy-button');
    if (!addButton) return;

    const messageEl = this.getOrCreateMessageEl(itemEl, addButton);
    const isEligible = subtotalCents >= tierPriceCents;

    addButton.disabled = !isEligible || anyGiftInCart;
    addButton.classList.toggle('is-disabled', !isEligible || anyGiftInCart);

    if (anyGiftInCart) {
      messageEl.textContent = window.rebuyOverwrites.alreadyAddedMessage;
      return;
    }

    if (isEligible) {
      messageEl.textContent = '';
      return;
    }

    const amountToSpend = tierPriceCents - subtotalCents;
    messageEl.textContent = window.rebuyOverwrites.amountToSpendTemplate.replace(
      '__AMOUNT__',
      formatMoney(amountToSpend, window.shopMoneyFormat)
    );
  }

  /**
   * @method getOrCreateMessageEl
   * @description Returns the `[data-rebuy-gwp-message]` element for a gift
   * item, creating and inserting it right before the Add button on first use.
   * Rebuy doesn't render this element itself (it has no concept of our tier
   * pricing), so it has to be injected by this file; `rebuy.css` already
   * styles it (and hides it via `:empty` while it has no text).
   * @param {HTMLElement} itemEl - The gift item element.
   * @param {HTMLElement} addButton - The item's Add button, used as the insertion anchor.
   * @returns {HTMLElement} The message element, existing or newly created.
   */
  getOrCreateMessageEl(itemEl, addButton) {
    const existing = itemEl.querySelector('[data-rebuy-gwp-message]');
    if (existing) return existing;

    const messageEl = document.createElement('p');
    messageEl.setAttribute('data-rebuy-gwp-message', '');
    addButton.insertAdjacentElement('beforebegin', messageEl);
    return messageEl;
  }
}

// This whole GWP sync layer is only relevant when Rebuy overwrites the native cart
// (see `settings.rebuy_overwrite` / `html-head-js.liquid`).
if (window.rebuyOverwrite) {
  new rebuyGiftWithPurchase().init();
}
